import { noop } from '@saaga/shared/utils/noop.util.ts'
import { useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { Notification } from '@/components/notifications/notification.interface'
import { useDeleteConversation } from '@/hooks/use-delete-conversation.hook'
import { usePinConversation } from '@/hooks/use-pin-conversation.hook'
import { useRenameConversation } from '@/hooks/use-rename-conversation.hook'
import type { ConversationPinStateMap } from './sidebar-conversations.helper'
import type {
  AgentItemActions,
  ChatItemActions,
  ChatItemEditState,
  ProjectItemActions,
} from './sidebar-item-actions.interface'
import {
  createDefaultAgentActions,
  createDefaultProjectActions,
} from './sidebar-item-actions-defaults.util'
import {
  buildChatActions,
  buildChatDeleteDialog,
  buildChatEditState,
} from './use-app-layout-chat-state.helper'

export interface ChatDeleteDialogState {
  deletingConversationId: string | null
  isDeleting: boolean
  onConfirm: () => void
  onCancel: () => void
}

export interface AppLayoutActionsState {
  chatActions: ChatItemActions
  chatEditState: ChatItemEditState
  chatDeleteDialog: ChatDeleteDialogState
  projectActions: ProjectItemActions
  agentActions: AgentItemActions
}

export interface AppLayoutActionsHandlers {
  handleNotificationClick: (notification: Notification) => void
  handleMarkAllRead: () => void
}

export interface UseAppLayoutActionsReturn {
  state: AppLayoutActionsState
  handlers: AppLayoutActionsHandlers
}

export interface UseAppLayoutActionsParams {
  conversationPinStateMap: ConversationPinStateMap
}

export const useAppLayoutActions = ({
  conversationPinStateMap,
}: UseAppLayoutActionsParams): UseAppLayoutActionsReturn => {
  const navigate = useNavigate()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const renameConversation = useRenameConversation()
  const deleteConversation = useDeleteConversation()
  const pinConversation = usePinConversation()

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      if (notification.actionUrl) {
        navigate(`/${orgSlug}${notification.actionUrl}`)
      }
    },
    [navigate, orgSlug]
  )

  const handleMarkAllRead = useCallback(noop, [])

  const chatActions = useMemo(
    () =>
      buildChatActions({
        renameConversation,
        deleteConversation,
        pinConversation,
        conversationPinStateMap,
      }),
    [renameConversation, deleteConversation, pinConversation, conversationPinStateMap]
  )

  const chatDeleteDialog = useMemo(
    () => buildChatDeleteDialog(deleteConversation),
    [deleteConversation]
  )

  const chatEditState = useMemo(() => buildChatEditState(renameConversation), [renameConversation])

  const projectActions = useMemo(createDefaultProjectActions, [])
  const agentActions = useMemo(createDefaultAgentActions, [])

  return {
    state: { chatActions, chatEditState, chatDeleteDialog, projectActions, agentActions },
    handlers: { handleNotificationClick, handleMarkAllRead },
  }
}
